import {
  Directive,
  ElementRef,
  OnDestroy,
  OnInit,
  Renderer2,
  inject,
  input,
  output
} from '@angular/core';

import {
  AUTO_FIT_MAX_WIDTH,
  COLUMN_KEY_ATTRIBUTE,
  measureNaturalWidths,
  resolveAutoFitWidth
} from '@shared/utils/column-auto-fit';

export interface ColumnResizeEvent {
  columnKey: string;
  newWidth: number;
}

@Directive({
  selector: '[appColumnResizer]',
  standalone: true
})
export class ColumnResizerDirective implements OnInit, OnDestroy {
  private el = inject(ElementRef);
  private renderer = inject(Renderer2);

  columnKey = input.required<string>();
  minWidth = input<number>(50);
  maxWidth = input<number | null>(null);
  resizingDisabled = input<boolean>(false);

  columnResize = output<ColumnResizeEvent>();

  private resizing = false;
  private startX = 0;
  private startWidth = 0;
  private resizeHandle: HTMLElement | null = null;

  ngOnInit(): void {
    if (this.resizingDisabled()) {
      return;
    }

    this.renderer.setAttribute(
      this.el.nativeElement,
      COLUMN_KEY_ATTRIBUTE,
      this.columnKey()
    );
    this.createResizeHandle();
  }

  ngOnDestroy(): void {
    if (this.resizeHandle) {
      this.renderer.removeChild(this.el.nativeElement, this.resizeHandle);
    }
  }

  private createResizeHandle(): void {
    const th = this.el.nativeElement as HTMLTableCellElement;

    this.resizeHandle = this.renderer.createElement('div');

    const classes = [
      'column-resize-handle',
      'absolute',
      'translate-x-[3px]',
      'top-0',
      'right-0',
      'h-full',
      'w-[6px]',
      'z-10',
      'opacity-0',
      'hover:opacity-100',
      'bg-blue-400',
      'dark:bg-blue-400',
      'transition-opacity',
      'duration-base',
      'cursor-col-resize',
      'user-select-none'
    ];

    this.renderer.setAttribute(this.resizeHandle, 'class', classes.join(' '));

    this.renderer.listen(this.resizeHandle, 'mousedown', (e: MouseEvent) => {
      this.onMouseDown(e);
    });

    this.renderer.listen(this.resizeHandle, 'dblclick', (e: MouseEvent) => {
      this.onDoubleClick(e);
    });

    this.renderer.appendChild(th, this.resizeHandle);
  }

  private onDoubleClick(event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();

    this.columnResize.emit({
      columnKey: this.columnKey(),
      newWidth: this.autoFitWidth()
    });
  }

  /**
   * Fits the column to what is on screen. The body is virtualised, so only the rendered rows
   * can be measured — a double-click after scrolling may legitimately land on another width.
   */
  private autoFitWidth(): number {
    const th = this.el.nativeElement as HTMLTableCellElement;
    const root = th.closest('app-table') ?? th.ownerDocument;
    const cells = root.querySelectorAll<HTMLElement>(
      `[${COLUMN_KEY_ATTRIBUTE}="${CSS.escape(this.columnKey())}"]`
    );

    return resolveAutoFitWidth(measureNaturalWidths(Array.from(cells)), {
      min: this.minWidth(),
      max: this.maxWidth() ?? AUTO_FIT_MAX_WIDTH
    });
  }

  private onMouseDown(event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();

    this.resizing = true;
    this.startX = event.pageX;

    const th = this.el.nativeElement as HTMLTableCellElement;
    this.startWidth = th.offsetWidth;

    this.renderer.addClass(th, 'resizing');

    this.renderer.setStyle(document.body, 'cursor', 'col-resize');
    this.renderer.setStyle(document.body, 'user-select', 'none');

    document.addEventListener('mousemove', this.onMouseMove);
    document.addEventListener('mouseup', this.onMouseUp);
  }

  /**
   * Width follows the pointer on the dragged header cell alone; the row is a CSS grid whose
   * tracks come from the column config, so this is a preview that {@link releasePreview}
   * hands back to the grid once the new width is committed.
   */
  private onMouseMove = (event: MouseEvent): void => {
    if (!this.resizing) return;

    const diff = event.pageX - this.startX;
    let newWidth = this.startWidth + diff;

    const min = this.minWidth();
    const max = this.maxWidth();

    if (newWidth < min) {
      newWidth = min;
    }

    if (max !== null && newWidth > max) {
      newWidth = max;
    }

    const th = this.el.nativeElement as HTMLTableCellElement;
    this.renderer.setStyle(th, 'width', `${newWidth}px`);
    this.renderer.setStyle(th, 'min-width', `${newWidth}px`);
    this.renderer.setStyle(th, 'max-width', `${newWidth}px`);
  };

  private onMouseUp = (): void => {
    if (!this.resizing) return;

    this.resizing = false;

    const th = this.el.nativeElement as HTMLTableCellElement;
    const newWidth = th.offsetWidth;

    this.columnResize.emit({
      columnKey: this.columnKey(),
      newWidth: newWidth
    });
    this.releasePreview();

    this.renderer.removeClass(th, 'resizing');
    this.renderer.removeStyle(document.body, 'cursor');
    this.renderer.removeStyle(document.body, 'user-select');

    document.removeEventListener('mousemove', this.onMouseMove);
    document.removeEventListener('mouseup', this.onMouseUp);
  };

  /**
   * Gives the cell back to its grid track. Keeping the dragged width inline would freeze the
   * cell — and with it the handle, which is positioned on the cell's right edge — at a width
   * the column config can no longer move.
   */
  private releasePreview(): void {
    const th = this.el.nativeElement as HTMLTableCellElement;
    this.renderer.removeStyle(th, 'width');
    this.renderer.removeStyle(th, 'min-width');
    this.renderer.removeStyle(th, 'max-width');
  }
}
